import { useDispatch, useSelector } from "react-redux";
import {
  getPricingData,
  getPricingIsFetching,
  getSummary,
  getTicketsQuantity,
} from "../../../ShopPage/redux/selectors/ShopSelectors";
import {
  getPricingRequest,
  summaryAddTicket,
  summaryRemoveTicketRate,
  summarySubtractTicket,
} from "../../../ShopPage/redux/actions/shopActions";
import { getExperienceDetail } from "../../redux/selectors/ExperiencesSelector";
import { useEffect } from "react";
import { isEmpty } from "lodash";
import { toISOFormatString } from "../../../../helpers/tools";
import { listTypes } from "../../../ShopPage/utils";
import { getAvailabilityByDate } from "../../redux/selectors/availabilitySelector";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collapseListingForPriceMode,
  getMainParticipantRate,
  getParticipantRates,
  getUnofferedTicketRates,
  isPerService,
} from "../../helpers/priceMode";
import { fitsCapacityUsage } from "../../helpers/capacityUsage";
import { RENTALS, isRental } from "../../../../helpers/rentals";
import { getExperiencePromotion } from "../../list/helpers/helpers";
import { spPromotionExperiencesList } from "../../config/spAventuasConfig";
import useWebsiteCurrency from "../../../../hooks/useWebsiteCurrency";
import useWebsiteRegion from "../../../../hooks/useWebsiteCountry";
import useDiscounts from "../../../ShopPage/containers/PricingContainer/hooks/useDiscounts";

const AddParticipantContainer = ({
  children,
  experienceId,
  period,
  supplierId,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["experiences"]);
  const { currency } = useWebsiteCurrency();
  const pricingData = useSelector(getPricingData);
  const { region } = useWebsiteRegion();
  const isPricingFetching = useSelector(getPricingIsFetching);
  const { tickets, dates, startTimeId } = useSelector(getSummary);
  const [validationMessage, setValidationMessage] = useState([]);
  const [isSelectedDate, setIsSelectedDate] = useState(false);
  const availableDate = useSelector((state) =>
    getAvailabilityByDate(state, startTimeId, dates.from)
  );
  const experience = useSelector((state) =>
    getExperienceDetail(state, experienceId)
  );
  const ticketsQuantity = useSelector((state) => getTicketsQuantity(state));
  const spDiscount =
    isRental(supplierId, RENTALS.spAventuras) &&
    region === "argentina" &&
    getExperiencePromotion(experienceId, spPromotionExperiencesList);
  const { discountCode } = useDiscounts();

  const participantRate = getMainParticipantRate(experience);
  const participantOptions = getParticipantRates(experience);

  // Se espera a tener la experiencia: hasta entonces no se sabe qué tarifas
  // ofrece y se estaría contando pasajeros de la anterior. El selector devuelve
  // `null` mientras el detalle que hay en el store no sea el de esta pantalla.
  useEffect(() => {
    if (!experience) return;

    // El summary es global y no se limpia al cambiar de experiencia: las
    // tarifas que ésta no ofrece se descartan antes de cotizar. Sin esto, el
    // `service` que quedó de una salida por servicio viaja en el listing de una
    // `perPerson` y el pricing responde 500.
    getUnofferedTicketRates(tickets, participantOptions).forEach((rate) =>
      dispatch(summaryRemoveTicketRate(rate))
    );

    if (!tickets[participantRate]) {
      dispatch(summaryAddTicket(participantRate));
    }
  }, [experience?._id]);
  useEffect(() => {
    // Sin la experiencia no hay `priceMode` con qué armar el listing, y los
    // tickets que haya todavía pueden ser los de la pantalla anterior.
    if (!isEmpty(tickets) && experience) {
      const sanitizedVariants = collapseListingForPriceMode(
        Object.entries(tickets).map(([rate, quantity]) => ({
          id: experienceId,
          rate,
          type: listTypes.EXPERIENCE,
          quantity,
        })),
        experience.priceMode
      );
      pricingRequest(sanitizedVariants);
    }
  }, [
    JSON.stringify(dates.to),
    JSON.stringify(dates.from),
    tickets,
    JSON.stringify(spDiscount),
    JSON.stringify(discountCode),
    experience?._id,
  ]);

  const checkAvailability = () => {
    const errorMsg = [];

    // Fail-closed: si hay un turno elegido tiene que haber disponibilidad
    // contra qué validarlo. Sin esto, el turno que queda seleccionado mientras
    // carga otro mes pasa la validación sin que se mire nada.
    if (!availableDate) {
      if (startTimeId) errorMsg.push(t("error.selectDateAndTime"));
      return errorMsg;
    }

    // Un turno bloqueado o que no le entra a esta experiencia se rechaza con
    // texto propio: sin él diría "el máximo es de 0 personas", que es cierto
    // pero no explica nada. Tampoco se revela quién tomó el turno.
    if (availableDate.blocked || availableDate.available <= 0) {
      errorMsg.push(t("error.slotUnavailable"));
    } else if (!fitsCapacityUsage(availableDate, experience?.capacityUsage)) {
      errorMsg.push(t("error.slotNotEnoughSeats"));
    } else if (availableDate.available < ticketsQuantity)
      errorMsg.push(
        t("error.exceedAvailableQuota", { quantity: availableDate.available })
      );
    if (availableDate.minCapacity > ticketsQuantity)
      errorMsg.push(
        t("error.notMeetMinCapacity", { quantity: availableDate.minCapacity })
      );

    return errorMsg;
  };

  const checkDateAndTime = () => {
    const errorMsg = [];

    if (!isSelectedDate) errorMsg.push(t("error.selectDateAndTime"));
    if (!startTimeId && isSelectedDate) errorMsg.push(t("error.selectTime"));

    return errorMsg;
  };

  // El titular es el primer pasajero, se llame como se llame su rate. En
  // `perService` no se pide un adulto porque no hay categorías que pedir.
  const checkMainMember = () =>
    tickets[participantRate]
      ? []
      : [
          t(
            isPerService(experience?.priceMode)
              ? "error.mainParticipantIsRequired"
              : "error.mainAdultIsRequired"
          ),
        ];

  useEffect(() => {
    const availabilityValidation = checkAvailability();
    const dateAndTimeValidation = checkDateAndTime();
    const mainMember = checkMainMember();
    const validations = [
      dateAndTimeValidation,
      mainMember,
      availabilityValidation,
    ]
      .filter((validation) => !isEmpty(validation))
      .flat();
    setValidationMessage(validations);
  }, [isSelectedDate, startTimeId, tickets]);

  const pricingRequest = (sanitizedVariants) => {
    const allowDiscounts = true;
    dispatch(
      getPricingRequest(
        toISOFormatString(dates.from),
        toISOFormatString(dates.to),
        period,
        sanitizedVariants,
        {},
        currency,
        allowDiscounts,
        listTypes.EXPERIENCE,
        spDiscount?.discountCode ?? discountCode
      )
    );
  };

  const ageSettings = {
    child: { min: 1, max: experience?.childAgeSetting },
  };

  const handleOnAddParticipant = (rate) => {
    dispatch(summaryAddTicket(rate));
  };

  const handleOnRemoveParticipant = (rate) => {
    dispatch(summarySubtractTicket(rate));
  };

  return children({
    handleOnAddParticipant,
    handleOnRemoveParticipant,
    participantOptions: participantOptions.filter(Boolean),
    mainRate: participantRate,
    ageSettings,
    tickets,
    startTimeId,
    pricingData,
    isPricingFetching,
    validationMessage,
    isSelectedDate,
    setIsSelectedDate,
  });
};

export default AddParticipantContainer;
